/* eslint-disable jira/jira-ssr/no-unchecked-globals-usage */

export type HydrationOutcomeStats = {
	/**
	 * the original dom count
	 */
	original: number;
	/**
	 * the number of nodes that were preserved during hydration
	 */
	preserved: number;
	/**
	 * the number of nodes that were added since capture
	 */
	added: number;
	/**
	 * the number of nodes that were removed likely due to hydration mismatch
	 */
	removed: number;
	/**
	 * the number of nodes that were removed for "other reasons" (not due to hydration mistmatch)
	 */
	unmounted: number;
	/**
	 * the number of failed suspense boundaries detected
	 */
	failedBoundaries: number;

	preservationRatio: number;

	removedNodes: Map<Node, Node>;
	unmountedNodes: Map<Node, Node>;
};

export type HydrationOutcomeReporter = () => HydrationOutcomeStats;

const allRemoved = (nodes: Node[]) => nodes.every((n) => !n.parentNode);

export function captureDocumentState(container: Node): HydrationOutcomeReporter {
	const parents = new Map<Node, Node | null>();
	const nodeToBoundary = new Map<Node, Node>();
	const boundaries = new Map<Node, Node[]>();
	function collectAllNodes(root: Node): Node[] {
		const out: Node[] = [];
		const walker = document.createTreeWalker(root, NodeFilter.SHOW_ALL);
		const stack: string[] = [];
		const boundaryTracker: Node[] = [];

		while (walker.nextNode()) {
			const node = walker.currentNode;

			// special handling for comments to detect start and end of failed suspense blocks
			if (node.nodeType === Node.COMMENT_NODE) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const commentContent = (node as Comment).data.trim();
				// Detect start of failed suspense block
				if (commentContent === '$!' || commentContent === '$') {
					stack.push(commentContent);
					boundaryTracker.push(node);
					boundaries.set(node, []);
				}

				// Detect end of failed suspense block
				if (commentContent === '/$') {
					stack.pop();
					boundaryTracker.pop();
				}
				// always jump over any comments
				// eslint-disable-next-line no-continue
				continue;
			}

			if (stack.includes('$!')) {
				// we are inside a failed suspense block, skip everything until we reach the end
				// these nodes will be removed by React anyway and we dont want to "focus" on here
				// the goal is to support "normal" hydration preservation
				// eslint-disable-next-line no-continue
				continue;
			}

			// ignore "text" nodes
			if (node.nodeType === Node.TEXT_NODE) {
				// eslint-disable-next-line no-continue
				continue;
			}

			// ignore "template" nodes (part of failed suspense and testing harness)
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'TEMPLATE') {
				// eslint-disable-next-line no-continue
				continue;
			}

			out.push(node);
			parents.set(node, node.parentNode);

			// connect node to the current "boundary"
			const currentBoundary = boundaryTracker[boundaryTracker.length - 1];
			if (currentBoundary) {
				nodeToBoundary.set(node, currentBoundary);
				if (node.parentNode === currentBoundary.parentNode) {
					boundaries.get(currentBoundary)?.push(node);
				}
			}
		}
		if (stack.length > 0) {
			throw new Error('Mismatched suspense comments, found more start than end');
		}
		return out;
	}

	// Capture pre-hydration snapshot
	const originalNodes = collectAllNodes(container);
	const originalSet = new Set(originalNodes);

	return () => {
		const currentNodes = collectAllNodes(container);
		const currentSet = new Set(currentNodes);

		const removedNodes: Node[] = [];
		const unmountedNodes: Node[] = [];
		const brokenBoundaries = new Set<Node>();

		for (const n of originalSet) {
			if (!currentSet.has(n)) {
				// check if this node was unmounted as part of a failed suspense boundary
				const boundaryId = nodeToBoundary.get(n);
				const boundaryElements = boundaryId && boundaries.get(boundaryId);
				if (boundaryId && (!boundaryElements || allRemoved(boundaryElements))) {
					// it was removed as part of hydration mismatch
					removedNodes.push(n);
					brokenBoundaries.add(boundaryId);
				} else {
					// it was preserved during hydration and removed later for some other reason.
					unmountedNodes.push(n);
				}
			}
		}

		const addedNodes: Node[] = [];
		for (const n of currentSet) {
			if (!originalSet.has(n)) {
				addedNodes.push(n);
			}
		}

		const findPreservedParents = (affectedNodes: Node[]) =>
			new Map(
				affectedNodes.flatMap<[Node, Node]>((node) => {
					const parent = parents.get(node) ?? null;
					if (!parent || !container.contains(parent)) {
						// we do not report any "inner" node focusing on the "edge"
						return [];
					}
					return [[node, parent]];
				}),
			);

		const topLevelRemovedNodes = findPreservedParents(removedNodes);
		const topLevelUnmountedNodes = findPreservedParents(unmountedNodes);

		const original = originalSet.size;
		const removed = removedNodes.length;
		const unmounted = unmountedNodes.length;
		const added = addedNodes.length;
		const preserved = original - removed;

		// clear
		parents.clear();
		boundaries.clear();
		nodeToBoundary.clear();

		originalNodes.length = 0;
		originalSet.clear();

		return {
			original,
			preserved,
			removed,
			unmounted,
			added,
			failedBoundaries: brokenBoundaries.size,
			preservationRatio: preserved / original,
			removedNodes: topLevelRemovedNodes,
			unmountedNodes: topLevelUnmountedNodes,
		};
	};
}
